import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import Grid from '@mui/material/Unstable_Grid2';
import _ from 'lodash';

import OrderLabel from '../../layout/OrderLabel';
import OrderNotes from '../../layout/OrderNotes';
import OrderEmail from '../../layout/OrderEmail';
import { TAKEOUT } from '../../../util/Constants';
import useClasses from '../../../hooks/legacy/useClasses';
import OrderAddress from '../../layout/OrderAddress';
import OrderPayment from '../../layout/OrderPayment';
import useScrollRestoration from '../../../hooks/legacy/useScrollRestoration';
import TakeOutOrderBox from '../../layout/TakeOutOrderBox/TakeOutOrderBox';
import CheckoutCartFormComponent from '../../input/CheckoutCartFormComponent';
import * as orderActions from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { updateAddressRequest } from '../../../redux/legacy/reducers/ducks/legacy/ProfileDuck';
import { toggleGlobalLoaderQueue } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import { getStripeRequest } from '../../../redux/legacy/reducers/ducks/legacy/StripeDuck';
import {
  getGCAvailableRequest,
  redeemGCRequest,
} from '../../../redux/legacy/reducers/ducks/legacy/GiftDuck';
import {
  setCheckoutAdjustRequest,
  placeOrder,
} from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import useSeo from '../../../hooks/useSeo';

import styles from './Checkout.style';

function Checkout() {
  useScrollRestoration();
  const dispatch = useDispatch();
  const classes = useClasses(styles);
  const navigate = useNavigate();
  const { setPageTitle } = useSeo();

  const [currentCart, setCurrentCart] = useState(null);
  const [adjustValues, setAdjustValue] = useState({});
  const [addressValues, setAddressValue] = useState({});
  const [onCheckoutUpdating, setOnCheckoutUpdating] = useState(false);
  const [onPlaceOrder, setOnPlaceOrder] = useState(false);
  const [justAdjusted, setJustAdjusted] = useState(true);
  const [onAddressUpdating, setOnAddressUpdating] = useState(false);
  const [addressJustUpdated, setAddressJustUpdated] = useState(true);
  const {
    cart,
    checkoutData,
    restaurant,
    restaurantId,
    orderType,
    checkoutAdjustResponse,
    addressUpdateResponse,
    address,
    availableGCData,
  } = useSelector(
    ({
      order,
      restaurant: selectedRestaurant,
      profile,
      address: selectedAddress,
      gift,
    }) => ({
      cart: order.setCartResponse,
      checkoutData: order.checkoutData,
      checkoutAdjustResponse: order.checkoutAdjustResponse,
      restaurantId: selectedRestaurant.selectedRestaurantId,
      restaurant: selectedRestaurant.selectedRestaurantData,
      orderType: order?.type,
      addressUpdateResponse: profile?.addressUpdateResponse,
      address: selectedAddress?.selectedAddress,
      availableGCData: gift.availableData,
    })
  );

  const isTakeout = orderType?.label === TAKEOUT;

  const onRedeemGCCode = (code) => {
    if (code) {
      dispatch(redeemGCRequest(code));
    }
  };

  const onAddressValue = (key, value) => {
    const newAddressValues = { ...addressValues };
    if (_.isNil(value) || value === '') {
      delete newAddressValues[key];
    } else {
      newAddressValues[key] = value;
    }

    setAddressJustUpdated(false);
    setAddressValue(newAddressValues);
  };

  const onAdjustedValue = (key, value) => {
    const newAdjustValues = { ...adjustValues };
    if (_.isNil(value) || value === '') {
      delete newAdjustValues[key];
    } else {
      newAdjustValues[key] = value;
    }

    let adjustNow = false;
    if (!adjustValues) {
      setAdjustValue(newAdjustValues);
    } else if (!_.isEqual(newAdjustValues, adjustValues)) {
      setAdjustValue(newAdjustValues);

      if (
        key === 'coupons' ||
        key === 'fees' ||
        key === 'delivery_option' ||
        key === 'gift_certificate'
      ) {
        adjustNow = true;
      }
    }

    setJustAdjusted(false);
    if (adjustNow) {
      setOnCheckoutUpdating(true);
      setJustAdjusted(true);
      dispatch(setCheckoutAdjustRequest(newAdjustValues));
    }
  };

  const completeCheckout = () => {
    const paymentType = checkoutData?.payments[0].type;

    if (paymentType === 'stripe') {
      dispatch(
        getStripeRequest({
          success_url: `${window.location.origin}/order-success/${checkoutData.id}/${checkoutData.restaurant}`,
          cancel_url: `${window.location.origin}/restaurant/${restaurantId}`,
          checkout: checkoutData.id,
          client: restaurantId,
          showError: (message) => {
            Notification.error(message, {
              toastId: 'PlaceOrderError',
            });
          },
        })
      );
    } else {
      dispatch(
        placeOrder({
          callback: () => {
            navigate(
              `/order-success/${checkoutData?.id}/${checkoutData.restaurant}`
            );
          },
          showError: (message) => {
            Notification.error(message, {
              toastId: 'PlaceOrderError',
            });
          },
        })
      );
    }
  };

  const onCompleteCheckout = () => {
    setOnCheckoutUpdating(true);
    if (!_.isEmpty(adjustValues) && !justAdjusted) {
      setOnPlaceOrder(true);
      setJustAdjusted(true);
      dispatch(setCheckoutAdjustRequest(adjustValues));
    } else if (!_.isEmpty(addressValues) && !addressJustUpdated) {
      setOnAddressUpdating(true);
      setAddressJustUpdated(true);
      setOnPlaceOrder(true);
      dispatch(
        updateAddressRequest({
          address: address.address_id,
          data: addressValues,
        })
      );
    } else {
      completeCheckout();
    }
  };

  useEffect(() => {
    setPageTitle('Checkout');

    dispatch(toggleGlobalLoaderQueue({ name: 'checkout', status: true }));
    setOnCheckoutUpdating(true);
    dispatch(orderActions.checkoutStart());

    return () => {
      setOnCheckoutUpdating(false);
      setCurrentCart(null);
      setAdjustValue({});
      setAddressValue({});
      setOnCheckoutUpdating(false);
      setOnPlaceOrder(false);
      dispatch(toggleGlobalLoaderQueue({ name: 'checkout', status: false }));
    };
  }, []);

  useEffect(() => {
    if (!currentCart) {
      setCurrentCart(cart);
    } else {
      setCurrentCart(cart);
      dispatch(toggleGlobalLoaderQueue({ name: 'checkout', status: true }));
      setOnCheckoutUpdating(true);
      dispatch(orderActions.checkoutStart());
    }
  }, [cart]);

  useEffect(() => {
    if (checkoutData && onCheckoutUpdating) {
      dispatch(
        toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      setOnCheckoutUpdating(false);
    }
  }, [checkoutData]);

  useEffect(() => {
    if (!addressUpdateResponse) {
      return;
    }

    if (onAddressUpdating) {
      setOnAddressUpdating(false);
      if (onPlaceOrder) {
        onCompleteCheckout();
      }
    }
  }, [addressUpdateResponse]);

  useEffect(() => {
    if (!checkoutAdjustResponse) {
      return;
    }

    if (onCheckoutUpdating) {
      if (!checkoutAdjustResponse?.error) {
        if (onPlaceOrder) {
          setOnPlaceOrder(false);
          onCompleteCheckout();
        } else {
          dispatch(
            toggleGlobalLoaderQueue({
              name: 'checkout',
              status: false,
            })
          );
          setOnCheckoutUpdating(false);
        }
      }
    }

    if (checkoutAdjustResponse?.error) {
      const errorMessage = checkoutAdjustResponse?.errorMessage;
      if (errorMessage) {
        if (errorMessage.toLowerCase().includes('coupon not valid')) {
          const { coupons, ...data } = adjustValues;
          setAdjustValue(data);
        }
      }
      dispatch(orderActions.setCheckoutAdjustResponse(null));

      dispatch(
        toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      setOnCheckoutUpdating(false);
    }
  }, [checkoutAdjustResponse]);

  useEffect(() => {
    if (checkoutData?.id && availableGCData.trigger) {
      dispatch(getGCAvailableRequest(checkoutData.id));
    }
  }, [checkoutData?.id, availableGCData.trigger]);

  const newLocal = (
    <Box
      sx={{ my: 1, p: 2, minHeight: '100vh' }}
      className={classes.parentCartWidth}
    >
      <Typography color="text.secondary" className={classes.restaurantTypeFont}>
        Your {orderType.label} From
      </Typography>
      <Typography className={classes.restaurantNameFont}>
        {restaurant?.name}
      </Typography>
      {isTakeout && <TakeOutOrderBox />}
      <Typography color="text.primary" className={classes.itemsPadding}>
        Your Items
      </Typography>
      <CheckoutCartFormComponent
        onAdjustedValue={onAdjustedValue}
        onCheckoutUpdating={onCheckoutUpdating}
      />
    </Box>
  );

  return (
    <Box
      variant="main"
      mt={4}
      data-test-id="checkout-page"
      sx={{ overflowX: 'hidden' }}
    >
      <Grid container spacing={3} mb={4}>
        <Grid xs={12} sm={8} lg={8}>
          <Typography ml={1} variant="h5" sx={{ fontWeight: 600 }}>
            Checkout
          </Typography>
          <OrderLabel />
          <OrderAddress
            onAdjustedValue={onAdjustedValue}
            onAddressValue={onAddressValue}
            onCheckoutUpdating={onCheckoutUpdating}
          />
          <OrderEmail
            onAdjustedValue={onAdjustedValue}
            onCheckoutUpdating={onCheckoutUpdating}
          />
          <OrderNotes
            onAdjustedValue={onAdjustedValue}
            onCheckoutUpdating={onCheckoutUpdating}
          />
          <OrderPayment
            onCompleteCheckout={onCompleteCheckout}
            onAdjustedValue={onAdjustedValue}
            onCheckoutUpdating={onCheckoutUpdating}
            redeemGCCode={onRedeemGCCode}
          />
        </Grid>
        <Grid xs={12} sm={12} lg={4}>
          {newLocal}
        </Grid>
      </Grid>
    </Box>
  );
}

export default Checkout;
